import { FdsIcon } from '@tcc/fds';
import { getMessage } from '@tcc/i18n';
import { useMemo } from 'react';
import { useCropPlanningAccess } from '../../contexts/CropPlanningAccessContext';
import { FeatureAsset } from '../../types/assets';
import { ImpersonationMode } from '../../types/impersonationModes';
import { DeferredLinkDescriptor, LinkDescriptor, MenuDescriptor, UrlPlaceholders } from './types';
import { UserRole } from '../../types/user';

export const useLinkDescriptors = () => {
  const { canAccess: canAccessCropPlanning } = useCropPlanningAccess();
  const menues: MenuDescriptor[] = useMemo(
    () => [
      {
        id: 'plans-menu',
        leadingIcon: FdsIcon.FactCheck,
        label: getMessage('sidenav.plans'),
        excludedImpersonationModes: [ImpersonationMode.DEALER_PROXYING_FARMER],
        order: 20,
        links: [
          {
            label: getMessage('sidenav.plans.allPlans'),
            href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/crop-planning/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-all-plans',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
            canAccess: canAccessCropPlanning,
          },
          {
            label: getMessage('sidenav.plans.recommendations'),
            href: `${UrlPlaceholders.EXPERTO_URL}/hybrid-crop-protection-recommendations/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-recommendations',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
          },
          {
            label: getMessage('sidenav.plans.weedFinder'),
            href: `${UrlPlaceholders.EXPERTO_URL}/weed-finder/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-weed-finder',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
          },
          {
            label: getMessage('sidenav.plans.productCatalog'),
            href: `${UrlPlaceholders.EXPERTO_URL}/product-catalog/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-product-catalog',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
          },
          {
            label: getMessage('sidenav.plans.seedQuality'),
            href: `${UrlPlaceholders.EXPERTO_URL}/seed-quality/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-seed-quality',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
          },
          {
            label: getMessage('sidenav.scripts.nitrogenRecommendation'),
            href: `${UrlPlaceholders.EXPERTO_URL}/n-smart/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-nitrogen-recommendation',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
          },
        ],
      },

      {
        id: 'fields-menu',
        leadingIcon: FdsIcon.Map,
        label: getMessage('sidenav.fields'),
        order: 50,
        links: [
          {
            label: getMessage('sidenav.fields.layers'),
            href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/field-selection-web${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-field-layers',
            requiredFeatureFlags: ['web.in-season'],
            excludedByFeatureFlag: ['web.fieldview.experto-sidenav-links'],
            isActiveRegexs: [/field-selection-web/i, /dmb/i],
          },
          {
            label: getMessage('sidenav.fields.inSeason'),
            href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/in-season${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-field-in-season',
            requiredFeatureFlags: ['web.in-season'],
            excludedByFeatureFlag: ['web.fieldview.experto-sidenav-links'],
            isActiveRegexs: [/in-season/i],
          },
          {
            label: getMessage('sidenav.fields.existingFields'),
            href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/field-selection-web${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-existing-fields',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
          },
          {
            label: getMessage('sidenav.fields.individualAnalysis'),
            href: `${UrlPlaceholders.EXPERTO_URL}/dekalb-tracking/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-individual-analysis',
            requiredFeatureFlags: ['web.fieldview.experto-sidenav-links'],
          },
        ],
      },
      {
        id: 'data',
        label: getMessage('sidenav.data'),
        leadingIcon: FdsIcon.Folder,
        order: 80,
        links: [
          {
            label: getMessage('sidenav.UploadAndImport'),
            href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/data-inbox/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-import',
            allowedFeatureAssets: [FeatureAsset.DATA_INBOX],
            isActiveRegexs: [/data-inbox/i],
          },
          {
            label: getMessage('sidenav.fix'),
            href: `${UrlPlaceholders.ROOT_URL}/static/fixit-tool/index.html${UrlPlaceholders.QUERY_PARAMS}`,
            excludedByFeatureFlag: [
              'web.fieldview-new-arch-fixit-tool',
              'web.fv-analytics.ard-data',
            ],
            testId: 'farmer-nav-fix',
            isActiveRegexs: [/fixit/i],
          },
          {
            label: getMessage('sidenav.fix'),
            href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/fixit-tool/${UrlPlaceholders.QUERY_PARAMS}`,
            requiredFeatureFlags: ['web.fieldview-new-arch-fixit-tool'],
            excludedByFeatureFlag: ['web.fv-analytics.ard-data'],
            testId: 'farmer-nav-fix',
            isActiveRegexs: [/fixit/i],
          },
          {
            label: getMessage('sidenav.connect'),
            href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/partners/${UrlPlaceholders.QUERY_PARAMS}`,
            testId: 'farmer-nav-apps',
            requiredFeatureFlags: ['web.connect-available-countries'],
            allowedFeatureAssets: [FeatureAsset.CONNECT],
            allowedImpersonationModes: [
              ImpersonationMode.PROXYING_ITSELF,
              ImpersonationMode.IMPERSONATION_NOT_AVAILABLE,
            ],
            isActiveRegexs: [/partners/i],
          },
        ],
      },
      {
        id: 'experiments-labz',
        leadingIcon: FdsIcon.Experiment,
        label: getMessage('sidenav.experimentsLabz'),
        order: 100,
        links: [
          {
            label: 'Directo',
            href: `${UrlPlaceholders.ROOT_URL}/labz/directo/`,
            testId: 'farmer-nav-experiments-labz-directo',
            requiredFeatureFlags: ['directo.experiment-labz'],
            isActiveRegexs: [/directo/i],
          },
          {
            label: 'Farmview',
            href: `${UrlPlaceholders.ROOT_URL}/labz/farmview/`,
            testId: 'farmer-nav-experiments-labz-farmview',
            requiredFeatureFlags: ['farmview.experiment-labz'],
            isActiveRegexs: [/farmview/i],
          },
          {
            label: 'FieldView Advisor',
            href: `${UrlPlaceholders.ROOT_URL}/api/fvadvisor/`,
            testId: 'information-architecture-fv-advisor',
            requiredFeatureFlags: ['advisor.experiment-labz'],
            isActiveRegexs: [/fvadvisor/i],
          },
          {
            label: getMessage('sidenav.machinery'),
            href: `${UrlPlaceholders.ROOT_URL}/labz/machinery/`,
            testId: 'farmer-nav-experiments-labz-machinery',
            requiredFeatureFlags: ['machinery.experiment-labz'],
            isActiveRegexs: [/machinery/i],
          },
        ],
      },
    ],
    [canAccessCropPlanning],
  );

  const links: LinkDescriptor[] = useMemo(
    () => [
      {
        id: 'dashboard',
        label: getMessage('sidenav.dashboard'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/dashboard${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Home,
        testId: 'farmer-nav-dashboard',
        requiredFeatureFlags: ['web.dashboard'],
        isActiveRegexs: [/dashboard/i],
        order: 10,
      },
      {
        id: 'scripts',
        label: getMessage('sidenav.scripts'),
        href: `${UrlPlaceholders.ROOT_URL}/variable-rate/index.html${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Dashboard,
        testId: 'farmer-nav-prescriptions',
        allowedFeatureAssets: [FeatureAsset.MANUAL_SCRIPTING],
        excludedByFeatureFlag: [
          'script-creator-plugin-version',
          'script-creator-operational-table-view',
        ],
        queryParams: {
          'field-id': window.location.hash.split('/')?.[3],
        },
        isActiveRegexs: [/variable-rate/i, /script-creator/i],
        order: 30,
      },
      {
        id: 'scripts',
        label: getMessage('sidenav.scripts'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/variable-rate${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Dashboard,
        testId: 'farmer-nav-prescriptions',
        allowedFeatureAssets: [FeatureAsset.MANUAL_SCRIPTING],
        requiredFeatureFlags: ['script-creator-plugin-version'],
        excludedByFeatureFlag: [
          'script-creator-operational-table-view',
          'web.fieldview.experto-sidenav-links',
        ],
        queryParams: {
          'field-id': window.location.hash.split('/')?.[3],
        },
        isActiveRegexs: [/variable-rate/i, /script-creator/i],
        order: 30,
      },
      {
        id: 'scripts',
        label: getMessage('sidenav.scripts'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/script-creator${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Dashboard,
        testId: 'farmer-nav-prescriptions',
        allowedFeatureAssets: [FeatureAsset.MANUAL_SCRIPTING],
        requiredFeatureFlags: ['script-creator-operational-table-view'],
        isActiveRegexs: [/variable-rate/i, /script-creator/i],
        order: 30,
      },
      {
        id: 'fields',
        label: getMessage('sidenav.fields'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/field-selection-web${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Map,
        testId: 'farmer-nav-fields',
        excludedByFeatureFlag: [
          'web.in-season',
          'web.fieldview.experto-sidenav-links',
        ],
        isActiveRegexs: [/field-selection-web/i, /dmb/i],
        order: 40,
      },
      {
        id: 'reports',
        label: getMessage('sidenav.insights'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/reports/yield-report${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Analytics,
        testId: 'farmer-nav-reports',
        allowedFeatureAssets: [FeatureAsset.YIELD_ANALYSIS],
        isActiveRegexs: [/reports/i],
        order: 60,
      },
      {
        label: getMessage('sidenav.ask'),
        href: 'https://climate.com/static/field-view/#/ask-fieldview-web/',
        leadingIcon: FdsIcon.Help,
        testId: 'farmer-nav-askfieldview',
        requiredFeatureFlags: ['web.ask-fieldview-web'],
        isActiveRegexs: [/ask-fieldview-web/i],
      },
      {
        id: 'programs',
        label: getMessage('sidenav.programs'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/programs/${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Psychiatry,
        testId: 'farmer-nav-programs',
        requiredFeatureFlags: ['web.programs-plugin'],
        isActiveRegexs: [/programs/i],
        order: 90,
      },
      {
        id: 'analytics',
        label: getMessage('sidenav.analytics'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/analytics/${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Analytics,
        testId: 'farmer-nav-analytics',
        requiredFeatureFlags: ['data-hub'],
        isActiveRegexs: [/analytics/i],
        order: 70,
      },
      {
        id: 'operations',
        label: getMessage('sidenav.operationHub'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/operation-hub/${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.LocationAway,
        testId: 'farmer-nav-operations',
        requiredFeatureFlags: ['web.operation-hub'],
        isActiveRegexs: [/operation-hub/i],
        order: 110,
      },
      {
        id: 'fuse',
        label: 'Fuse',
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/fuse/${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.Experiment,
        testId: 'farmer-nav-fuse',
        requiredFeatureFlags: ['web.fuse'],
        isActiveRegexs: [/fuse/i],
        order: 120,
      },
      {
        id: 'sam',
        label: getMessage('sidenav.sam'),
        href: `${UrlPlaceholders.SUPPORT_URL}/chat`,
        leadingIcon: FdsIcon.FieldViewAI,
        testId: 'farmer-nav-sam',
        order: 130,
        target: '_blank',
        title: getMessage('sidenav.sam.title'),
        requiredFeatureFlags: ['web.fieldview.sam-sidenav'],
        allowedCountryCodes: ['US'],
        allowedRoles: [UserRole.SUPPORT],
      },
    ],
    [],
  );

  // Each deferred descriptor pairs a nav item with its async access condition.
  const deferredDescriptors: DeferredLinkDescriptor[] = useMemo(
    () => [
      {
        id: 'plans',
        label: getMessage('sidenav.plans'),
        href: `${UrlPlaceholders.ROOT_URL}/static/field-view/#/crop-planning/${UrlPlaceholders.QUERY_PARAMS}`,
        leadingIcon: FdsIcon.FactCheck,
        testId: 'farmer-nav-crop-planning',
        excludedByFeatureFlag: ['web.fieldview.experto-sidenav-links'],
        excludedImpersonationModes: [ImpersonationMode.DEALER_PROXYING_FARMER],
        isActiveRegexs: [/crop-planning/i],
        order: 200,
        canAccess: canAccessCropPlanning,
      },
    ],
    [canAccessCropPlanning],
  );

  return {
    linkDescriptors: links,
    menuDescriptors: menues,
    deferredDescriptors,
  };
};
